/**
 * Type-safe event bus for decoupled communication between systems
 */

export type EventCallback<T = unknown> = (data: T) => void;

export interface GameEvents {
  'game:start': void;
  'game:pause': void;
  'game:resume': void;
  'game:over': { winner: string };

  'unit:spawn': { entityId: number; factionId: string; unitType: string };
  'unit:death': { entityId: number; factionId: string; killedBy?: number };
  'unit:attack': { attackerId: number; targetId: number; damage: number; isCrit?: boolean };

  // 地形战斗效果
  'combat:dodge': { targetId: number; attackerId: number };
  'combat:stun': { targetId: number; duration: number };
  'combat:crit': { attackerId: number; targetId: number; damage: number };
  'combat:burn': { targetId: number; damage: number; duration: number };
  'combat:poison': { targetId: number; damage: number; duration: number };
  'combat:freeze': { targetId: number; duration: number };
  'spell:cast': {
    casterId: number;
    casterX: number;
    casterY: number;
    casterZ: number;
    targetX: number;
    targetY: number;
    targetZ: number;
    spellType: 'lightning' | 'aoe';
    targetCount: number;
  };
  'arrow:fire': {
    startX: number;
    startY: number;
    startZ: number;
    endX: number;
    endY: number;
    endZ: number;
  };
  'sword:slash': {
    x: number;
    y: number;
    z: number;
    rotation: number;
    targetX: number;
    targetY: number;
    targetZ: number;
    damage: number;
  };
  'spear:thrust': {
    x: number;
    y: number;
    z: number;
    rotation: number;
    targetX: number;
    targetY: number;
    targetZ: number;
    damage: number;
  };
  'cavalry:charge': {
    x: number;
    y: number;
    z: number;
    rotation: number;
    targetX: number;
    targetY: number;
    targetZ: number;
    damage: number;
    speed: number;  // Charge speed for effect intensity
  };

  'building:placed': { entityId: number; factionId: string; buildingType: string; x: number; z: number };
  'building:destroyed': { entityId: number; factionId: string };
  'building:upgrade': { entityId: number };
  'building:repair': { entityId: number };

  'wall:placed': { entityId: number; factionId: string; tileX: number; tileZ: number };
  'wall:destroyed': { entityId: number; tileX: number; tileZ: number };
  'wall:variant_changed': { entityId: number; variant: string; rotation: number };

  'territory:captured': { x: number; z: number; newOwner: string; oldOwner: string | null };

  'economy:income': { factionId: string; amount: number };
  'economy:spend': { factionId: string; amount: number; item: string };

  'camera:move': { x: number; z: number };
  'camera:zoom': { level: number };

  'input:click': { worldX: number; worldZ: number; button: number };
  'input:drag': { dx: number; dz: number };

  'selection:changed': { selectedCount: number; entities: number[] };
  'command:move': { x: number; z: number; unitCount: number };
  'command:attack': { targetId: number; unitCount: number; x?: number; z?: number };

  // Diplomacy events
  'diplomacy:war': { attacker: string; target: string };
  'diplomacy:peace': { from: string; to: string };
  'diplomacy:proposal': { from: string; to: string; type: string };
  'diplomacy:rejected': { from: string; to: string; type: string };
  'diplomacy:treaty': { type: string; parties: [string, string] };
  'diplomacy:broken': { type: string; breaker: string; other: string };
  'diplomacy:expired': { type: string; parties: [string, string] };

  // Tech events
  'tech:started': { factionId: string; techId: string };
  'tech:completed': { factionId: string; techId: string; techName: string };
  'tech:speedup': { factionId: string; techId: string; cost: number };
}

class EventBusImpl {
  private listeners: Map<string, Set<EventCallback<unknown>>> = new Map();

  on<K extends keyof GameEvents>(event: K, callback: EventCallback<GameEvents[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback<unknown>);

    return () => this.off(event, callback);
  }

  off<K extends keyof GameEvents>(event: K, callback: EventCallback<GameEvents[K]>): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback as EventCallback<unknown>);
    }
  }

  emit<K extends keyof GameEvents>(event: K, data: GameEvents[K]): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      for (const callback of eventListeners) {
        callback(data);
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const EventBus = new EventBusImpl();
